import React, { useState } from 'react';
import { Modal, Button, Form, Input, Table, message, Popconfirm } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useFormContext } from 'react-hook-form';

interface Cheque {
  id?: number;
  key: number;
  banco: string;
  agencia: string;
  conta: string;
  numero?: number;
  nomeDonoCheque?: string;
}

interface ChequeModalProps {
  parcelas: number;
  opened: boolean;
  onClose: () => void;
  shouldClearCheques?: boolean;
  chequeToDelete?: number | null;
  // Somente leitura: exibe apenas a lista de cheques, sem incluir/editar/excluir
  // (usado quando a etapa de Pagamento está bloqueada para o perfil do usuário).
  readOnly?: boolean;
  /**
   * Nome preenchido pelo botão de cópia (o responsável na matrícula, o aluno na
   * venda avulsa). Sem nome o botão não aparece.
   */
  nomeParaCopiar?: string;
  /** Rótulo do botão de cópia. */
  rotuloCopiar?: string;
}

const ChequeModal: React.FC<ChequeModalProps> = ({
  parcelas,
  opened,
  onClose,
  shouldClearCheques,
  chequeToDelete,
  readOnly = false,
  nomeParaCopiar,
  rotuloCopiar = 'Copiar Responsável',
}) => {
  const { setValue, getValues } = useFormContext<any>();

  const [cheques, setCheques] = useState<Cheque[]>([]);
  const [form] = Form.useForm();

  // Garantir que parcelas seja um número válido
  const parcelasSeguras = Number(parcelas) > 0 ? Number(parcelas) : 0;
  
  // Calcular quantidade restante de cheques
  const chequesRestantes = parcelasSeguras - cheques.length;

  // Efeito para limpar cheques quando shouldClearCheques for true
  React.useEffect(() => {
    if (shouldClearCheques) {
      setCheques([]);
      setValue('cheques', []);
      form.resetFields();
      form.setFieldsValue({ qtdCheques: parcelasSeguras });
    }
  }, [shouldClearCheques, parcelasSeguras, form, setValue]);

  // Efeito para carregar cheques já salvos no formulário ao abrir o modal.
  React.useEffect(() => {
    if (!opened) {
      return;
    }

    const chequesSalvos = getValues('cheques') ?? [];
    const chequesFormatados: Cheque[] = chequesSalvos.map((cheque: any, index: number) => ({
      id: cheque?.id,
      key: index + 1,
      nomeDonoCheque: cheque?.nomeDonoCheque,
      banco: cheque?.banco,
      agencia: cheque?.agencia,
      conta: cheque?.conta,
      numero: cheque?.numero,
    }));

    setCheques(chequesFormatados);
    const chequesRestantesIniciais = parcelasSeguras - chequesFormatados.length;
    form.setFieldsValue({ qtdCheques: chequesRestantesIniciais > 0 ? chequesRestantesIniciais : 0 });
  }, [opened, getValues, parcelasSeguras, form]);

  // Efeito para inicializar o valor de qtdCheques quando o modal abrir
  React.useEffect(() => {
    if (opened && parcelasSeguras > 0) {
      const chequesRestantesIniciais = parcelasSeguras - cheques.length;
      form.setFieldsValue({ qtdCheques: chequesRestantesIniciais > 0 ? chequesRestantesIniciais : 0 });
    }
  }, [opened, parcelasSeguras, cheques.length, form]);

  // Efeito para deletar cheque específico quando chequeToDelete for informado
  React.useEffect(() => {
    if (chequeToDelete !== null && chequeToDelete !== undefined) {
      setCheques(prevCheques => {
        const novosCheques = prevCheques.filter(cheque => cheque.key !== chequeToDelete);
        // Reordenar as chaves para manter sequência
        const chequesReordenados = novosCheques.map((cheque, index) => ({
          ...cheque,
          key: index + 1
        }));
        
        // Atualizar o campo qtdCheques após exclusão
        const novoRestante = parcelasSeguras - chequesReordenados.length;
        form.setFieldsValue({ qtdCheques: novoRestante > 0 ? novoRestante : 0 });
        
        return chequesReordenados;
      });
    }
  }, [chequeToDelete, parcelasSeguras, form]);

  const excluirCheque = (key: number) => {
    const novosCheques = cheques.filter(cheque => cheque.key !== key);
    // Reordenar as chaves para manter sequência
    const chequesReordenados = novosCheques.map((cheque, index) => ({
      ...cheque,
      key: index + 1
    }));
    setCheques(chequesReordenados);
    // Atualizar o campo qtdCheques após exclusão
    const novoRestante = parcelasSeguras - chequesReordenados.length;
    form.setFieldsValue({ qtdCheques: novoRestante > 0 ? novoRestante : 0 });
    message.success('Cheque excluído com sucesso!');
  };

  // Na matrícula o nome vem do responsável do formulário; nas demais telas quem
  // usa o modal informa o nome pela prop `nomeParaCopiar`.
  const nomeSugerido = nomeParaCopiar ?? getValues('responsavel.pessoaResponsavel.nome');

  const copiarResponsavel = () => {
    if (!nomeSugerido || nomeSugerido.trim() === '') {
      message.warning('Não existe dados de Responsável');
      return;
    }

    form.setFieldsValue({
      nomeDonoCheque: nomeSugerido
    });
  };

  const recuperarDadosAnteriores = () => {
    if (cheques.length === 0) {
      message.warning('Não existem dados anteriores para recuperar');
      return;
    }
    
    // Pegar o último cheque da lista
    const ultimoCheque = cheques[cheques.length - 1];
    
    // Preencher todos os campos exceto o número do cheque
    form.setFieldsValue({
      nomeDonoCheque: ultimoCheque.nomeDonoCheque,
      banco: ultimoCheque.banco,
      agencia: ultimoCheque.agencia,
      conta: ultimoCheque.conta,
      // Não preencher o número do cheque
      numero: 0
    });
    
    message.success('Dados anteriores recuperados com sucesso!');
  };

  const handleOk = () => {
    const chequesParaSalvar = cheques.map(({ key, ...cheque }) => cheque);
    setValue('cheques', chequesParaSalvar);
    onClose();
  };

  const addCheque = () => {
    form.validateFields().then((values) => {
      let qtdCheques = values.qtdCheques;
      let newCheques: Cheque[] = [];
      for (let i = 0; i < qtdCheques; i++) {
        const newCheque: Cheque = {
          key: cheques.length + i + 1,
          nomeDonoCheque: values.nomeDonoCheque,
          banco: values.banco, 
          agencia: values.agencia, 
          conta: values.conta,
          numero: Number(values.numero) + i,
        };
        newCheques.push(newCheque);
      }
      setCheques([...cheques, ...newCheques]);
      form.resetFields();
      // Atualizar o campo qtdCheques com o valor restante após adicionar
      const novoRestante = parcelasSeguras - (cheques.length + qtdCheques);
      form.setFieldsValue({ qtdCheques: novoRestante > 0 ? novoRestante : 0 });
    }).catch((errorInfo) => {
      // Não precisa fazer nada aqui, o Form já mostra as mensagens de erro
      console.log('Erro de validação:', errorInfo);
    });
  };

  const columns: ColumnsType<Cheque> = [
    {
      title: 'Dono do Cheque',
      dataIndex: 'nomeDonoCheque',
      key: 'nomeDonoCheque',
    },
    {
      title: 'Banco',
      dataIndex: 'banco',
      key: 'banco',
    },
    {
      title: 'Agência',
      dataIndex: 'agencia',
      key: 'agencia',
    },
    {
      title: 'Conta',
      dataIndex: 'conta',
      key: 'conta',
    },
    {
      title: 'Número do Cheque',
      dataIndex: 'numero',
      key: 'numero',
    },
    ...(readOnly
      ? []
      : [
          {
            title: 'Ação',
            key: 'acao',
            render: (_: unknown, record: Cheque) => (
              <Popconfirm
                title="Confirma a exclusão?"
                description="Tem certeza que deseja excluir este cheque?"
                onConfirm={() => excluirCheque(record.key)}
                okText="Sim"
                cancelText="Não"
              >
                <Button type="link" danger>
                  Excluir
                </Button>
              </Popconfirm>
            ),
          },
        ]),
  ];

  return (
    <>
      <Modal
        title={readOnly ? "Dados dos Cheques" : "Informar Dados do Cheque"}
        open={opened}
        onOk={readOnly ? onClose : handleOk}
        onCancel={onClose}
        footer={
          readOnly
            ? [
                <Button key="close" type="primary" onClick={onClose}>
                  Fechar
                </Button>,
              ]
            : [
                <Button key="back" onClick={onClose}>
                  Cancelar
                </Button>,
                <Button
                  key="submit"
                  type="primary"
                  onClick={handleOk}
                  disabled={cheques.length !== parcelasSeguras}
                >
                  Confirmar
                </Button>,
              ]
        }
      >
        <Form form={form} layout="vertical" hidden={readOnly}>
          <Form.Item
            name="nomeDonoCheque"
            label="Dono do Cheque"
            rules={[{ required: true, message: 'Por favor, insira o nome do Dono do Cheque!' }]}
          >
            <Input />
          </Form.Item>
          <Button
            type="default"
            onClick={copiarResponsavel}
            style={{ marginBottom: '16px' }}
          >
            {rotuloCopiar}
          </Button>
          <Form.Item
            name="banco"
            label="Banco"
            rules={[{ required: true, message: 'Por favor, insira o banco!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="agencia"
            label="Agência"
            rules={[{ required: true, message: 'Por favor, insira a agência!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="conta"
            label="Conta"
            rules={[{ required: true, message: 'Por favor, insira a conta!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="numero"
            label="Número do Cheque"
            rules={[{ required: true, message: 'Por favor, insira o número do cheque!' }]}
          >
            <Input type="number" />
          </Form.Item>
          <Form.Item
            name="qtdCheques"
            label="Qtd. de Cheques Sequenciais"
            rules={[{ required: true, message: 'Por favor, insira a quantidade de cheques sequencial!' },
              { 
                validator: (_, value) => {
                  if (parcelasSeguras === 0) {
                    return Promise.reject('Não há parcelas de cheque para informar!');
                  }
                  return value > chequesRestantes ? Promise.reject(`A quantidade máxima de cheques é ${chequesRestantes}!`) : Promise.resolve();
                }
              }
            ]}
            initialValue={chequesRestantes}
          >
            <Input type='number' key={chequesRestantes} />
          </Form.Item>
        </Form>
        <div style={{ marginBottom: '16px', display: readOnly ? 'none' : 'flex', gap: '8px' }}>
          <Button key="submit" type="primary" onClick={addCheque}>
            Adicionar
          </Button>
          {cheques.length > 0 && chequesRestantes > 0 && (
            <Button key="recover" type="default" onClick={recuperarDadosAnteriores}>
              Recuperar Dados Anteriores
            </Button>
          )}
          {cheques.length > 0 && (
            <Popconfirm
              title="Excluir todos os cheques?"
              description="Tem certeza que deseja excluir todos os cheques adicionados?"
              onConfirm={() => {
                setCheques([]);
                form.setFieldsValue({ qtdCheques: parcelasSeguras });
                message.success('Todos os cheques foram excluídos!');
              }}
              okText="Sim"
              cancelText="Não"
            >
              <Button key="deleteAll" danger>
                Excluir Todos
              </Button>
            </Popconfirm>
          )}
        </div>

        <Table columns={columns} dataSource={cheques} pagination={false} />
      </Modal>
    </>
  );
};

export default ChequeModal;